import { useState, useEffect, ReactElement } from "react";
import {
  TextField,
  Box,
  Typography,
} from "@mui/material";
import StandardDialog from "../../../components/StandardDialog";

interface EditRoleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (newRoleName: string) => Promise<void>;
  saving: boolean;
  currentRoleName: string;
  roleId: string;
}

function EditRoleDialog({
  open,
  onClose,
  onSave,
  saving,
  currentRoleName,
  roleId,
}: EditRoleDialogProps): ReactElement {
  const [roleName, setRoleName] = useState<string>(currentRoleName);
  const [error, setError] = useState<string>("");

  // Update role name when dialog opens or currentRoleName changes
  useEffect(() => {
    if (open) {
      setRoleName(currentRoleName);
      setError("");
    }
  }, [open, currentRoleName]);

  const handleSave = async (): Promise<void> => {
    // Validate role name
    if (!roleName.trim()) {
      setError("Role name is required");
      return;
    }

    if (roleName.trim().length < 2) {
      setError("Role name must be at least 2 characters");
      return;
    }

    if (roleName.trim().length > 50) {
      setError("Role name must be less than 50 characters");
      return;
    }

    // Check if name changed
    if (roleName.trim() === currentRoleName) {
      onClose();
      return;
    }

    setError("");
    await onSave(roleName.trim());
    
    // Reset form on success
    if (!saving) {
      setRoleName(currentRoleName);
    }
  };

  const handleClose = (): void => {
    if (!saving) {
      setRoleName(currentRoleName);
      setError("");
      onClose();
    }
  };

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title="Edit Role"
      confirmText="Save"
      onConfirm={handleSave}
      confirmDisabled={saving || !roleName.trim() || roleName.trim() === currentRoleName}
      disableClose={saving}
      maxWidth="sm"
    >
      <Box>
        <TextField
          fullWidth
          label="Role Name"
          value={roleName}
          onChange={(e) => {
            setRoleName(e.target.value);
            setError("");
          }}
          error={!!error}
          helperText={error || "Enter a new name for this role"}
          disabled={saving}
          autoFocus
        />
      </Box>
    </StandardDialog>
  );
}

export default EditRoleDialog;
